/* eslint-disable no-underscore-dangle */
import _ from 'lodash';
import Formula from '../NumericValues/Formula';

function isValueInAttribute(attribute, value, attributeSelection) {
  if (!_.isUndefined(attribute.formula)) {
    return _.isNil(attribute) || !attribute.isValidSelection(value, attributeSelection);
  }

  // eslint-disable-next-line no-param-reassign
  attribute._attributeValues = _.map(attribute._attributeValues, (attributeValue) => {
    // Currently it's possible to have formula's in the whiteList since their use wasn't well defined
    // If we find one try to evaluate it into a singular value based on the attribute domain. Else
    // return it as is.
    let returnAttribute = attributeValue;

    try {
      if (Formula.containsVariables(attributeValue.value)) {
        returnAttribute = { value: Formula.evaluate(attributeValue.value, attributeSelection) };
      }
    } catch (err) {
      // do nothing
    } finally {
      return returnAttribute; // eslint-disable-line no-unsafe-finally
    }
  });

  return _.isNil(attribute) || !attribute.contains(value);
}

function lowerDictionaryKeys(attributeSelection) {
  const lowerCaseAttributeSelection = {};
  _.forOwn(attributeSelection, (value, key) => {
    lowerCaseAttributeSelection[key.toLowerCase()] = value;
  });
  return lowerCaseAttributeSelection;
}

module.exports = class WhiteList {
  /**
   * @param  {Array<Object>} whitelist The array of attributes this white list is.
   */
  constructor(whitelist) {
    this._whiteList = {};

    _.each(whitelist, value => this.addAttribute(value));
  }

  getAttribute(key) {
    return this._whiteList[key.toLowerCase()];
  }

  addAttribute(attribute) {
    this._whiteList[attribute._key.toLowerCase()] = attribute;
  }

  /**
   * Returns the white list as an collection of attributes
   * @return {Attribute[]} the white list as a collection.
   */
  getAttributes() {
    return _.values(this._whiteList);
  }

  get _requiredAttributes() {
    return _.filter(this._whiteList, value => value.required);
  }

  /**
   * Determines which keys are not valid for the white list.
   * This could mean that the key is not in the white list or
   * that the key is in the white list but the value is not valid for that key.
   *
   * @param  {Object} attributeSelection Dictionary of selections
   * @return {string[]}  The list of keys that fail validation
   */
  identifyInvalidSelections(attributeSelection) {
    const keysNotInWhiteList = [];
    _.forOwn(attributeSelection, (value, key) => {
      const matchingAttribute = this.getAttribute(key);

      if (_.isNil(matchingAttribute)) {
        return;
      }

      if (isValueInAttribute(matchingAttribute, value, attributeSelection)) {
        keysNotInWhiteList.push({
          attributeKey: key,
          message: `${value} is not a valid selection for ${key}`,
        });
      }
    });

    return keysNotInWhiteList;
  }

  /**
   * Determines which keys are missing from the attribute selection.
   *
   * @param  {Object} attributeSelection Dictionary of selections
   * @return {string[]}  The list of attributes are not in the selection but are required to be
   */
  identifyMissingRequiredAttributes(attributeSelection) {
    const missingAttributes = [];

    const lowerCaseAttributeSelection = lowerDictionaryKeys(attributeSelection);

    _.each(this._requiredAttributes, (attribute) => {
      const matchingAttribute = lowerCaseAttributeSelection[attribute.key.toLowerCase()];
      if (_.isNil(matchingAttribute)) {
        missingAttributes.push(attribute);
      } else if (_.isUndefined(attribute.formula) && !attribute.contains(matchingAttribute)) {
        missingAttributes.push(attribute);
      }
    });
    return missingAttributes;
  }
};
